import { CloudUpload, Add } from "@material-ui/icons";
import * as React from "react";
import { WithIntlProps, injectIntl } from "react-intl";
import { SwissGermanLocale, SwissTimeZone } from "../../data/constants";
import { ExamSessionViewModel, GetLocalizedName } from "../../model/ExamSessionViewModel";
import { GetTranslation } from "../../translations/GetTranslation";
import CustomButton from "../common/buttons/CustomButton";
import { ColumnDefinition, CustomGrid, CustomGridFilter } from "../common/customGrid/CustomGrid";
import TranslatedMessage from "../translations/TranslatedMessage";
import { loadValueFromLocalStorage, saveValue } from "../../utils/StorageHelper";
import ButtonMenu from "../common/buttons/ButtonMenu";

export type ExamSessionAddType = "normal" | "life" | "builder";

type IExamSessionGridProps = {
  examSessions: ExamSessionViewModel[];
  onExamSessionClick: (id: string) => void;
  onAddClick: (type: ExamSessionAddType) => void;
  onImportPevButtonClick: () => void;
} & WithIntlProps<any>;

type ExamSessionGridState = {
  filters: CustomGridFilter<ExamSessionViewModel>[];
};

class ExamSessionGrid extends React.Component<IExamSessionGridProps, ExamSessionGridState> {
  constructor(props: any) {
    super(props);

    const { intl } = props;

    const filters: CustomGridFilter<ExamSessionViewModel>[] = [
      {
        label: GetTranslation(intl, "examSession.field.hidePastSessions"),
        isActive: loadValueFromLocalStorage("examSession.filter.0", false),
        filterFunction: (row: ExamSessionViewModel) => {
          let today = new Date();
          today.setHours(0, 0, 0, 0);
          let to = new Date(row.to);
          to.setHours(0, 0, 0, 0);

          if (to >= today) {
            return true;
          }

          return false;
        },
        onFilterChange: this.getHandlerByFilterIndex(0),
      },
      {
        label: GetTranslation(intl, "examSession.field.lifeSessionsOnly"),
        isActive: loadValueFromLocalStorage("examSession.filter.1", false),
        filterFunction: (row: ExamSessionViewModel) => {
          return row.isLife === true;
        },
        onFilterChange: this.getHandlerByFilterIndex(1),
      },
    ];

    this.state = {
      filters,
    };
  }

  private columnsDefinitions: Array<ColumnDefinition<ExamSessionViewModel>> = [
    {
      columnKey: "sessionKey",
      columnLabel: <TranslatedMessage id="examSession.field.sessionKey" />,
      cellValue: (_) => GetLocalizedName(_),
    },
    {
      columnKey: "from",
      columnLabel: <TranslatedMessage id="examSession.field.from" />,
      orderValue: (_) => _.from,
      cellValue: (_) => new Date(_.from).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone }),
    },
    {
      columnKey: "to",
      columnLabel: <TranslatedMessage id="examSession.field.to" />,
      orderValue: (_) => _.to,
      cellValue: (_) => new Date(_.to).toLocaleDateString(SwissGermanLocale, { timeZone: SwissTimeZone }),
    },
    {
      columnKey: "signupStartDate",
      columnLabel: <TranslatedMessage id="examSession.field.signupStartDate" />,
      orderValue: (_) => _.signupStartDate,
      cellValue: (_) => new Date(_.signupStartDate).toLocaleString(SwissGermanLocale, { timeZone: SwissTimeZone }),
    },
    {
      columnKey: "signupEndDate",
      columnLabel: <TranslatedMessage id="examSession.field.signupEndDate" />,
      orderValue: (_) => _.signupEndDate,
      cellValue: (_) => new Date(_.signupEndDate).toLocaleString(SwissGermanLocale, { timeZone: SwissTimeZone }),
    },
    {
      columnKey: "published",
      columnLabel: <TranslatedMessage id="common.field.status" />,
      orderValue: (_) => _.published,
      cellValue: (_) => {
        if (_.published) {
          return <TranslatedMessage id="common.status.published" />;
        }

        return <TranslatedMessage id="common.status.draft" />;
      },
    },
  ];

  private additionalButtons = () => (
    <>
     <ButtonMenu
        tooltip="examSession.button.new"
        icon={<Add />}
        items={[
          { key:  "normal", label: "examSession.button.new" },
          { key: "life", label: "examSession.button.newLife" },
          { key: "builder", label: "examSession.button.newBuilder" },
        ]}
        onSelect={(key) => this.props.onAddClick(key as ExamSessionAddType)}
      />
      <CustomButton
        color="primary"
        tooltip={<TranslatedMessage id="examSession.button.importPev" />}
        onClick={this.props.onImportPevButtonClick}
      >
        <CloudUpload />
      </CustomButton>
    </>
  );

  private getHandlerByFilterIndex(index: number) {
    return (checked: boolean) => {
      saveValue(`examSession.filter.${index}`, checked);
      this.setState((prevState) => {
        const newFilters = [...prevState.filters];
        newFilters[index].isActive = checked;
        return {
          ...prevState,
          filters: [...newFilters],
        };
      });
    };
  }

  private updateFilterLabel(filterIndex: number) {
    const { intl } = this.props;

    this.setState((prevState) => {
      const newFilterLabel = GetTranslation(intl, "examSession.field.hidePastSessions");
      if (newFilterLabel === prevState.filters[filterIndex].label) {
        return null;
      }

      const newFilters = [...prevState.filters];
      newFilters[filterIndex].label = newFilterLabel;
      return {
        ...prevState,
        filters: [...newFilters],
      };
    });
  }

  public componentDidUpdate() {
    this.updateFilterLabel(0);
  }

  public render() {
    return (
      <CustomGrid<ExamSessionViewModel>
        filters={this.state.filters}
        columnDefinitions={this.columnsDefinitions}
        rows={this.props.examSessions}
        onRowClick={(_) => this.props.onExamSessionClick(_.id)}
        rowKey={(_) => _.id}
        hideAddButton={true}
        addButtonTooltip={<TranslatedMessage id="examSession.button.new" />}
        onAddClick={this.props.onAddClick}
        additionalButtons={this.additionalButtons()}
      />
    );
  }
}

export default injectIntl(ExamSessionGrid);
